import { Component, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { PasswordModule } from 'primeng/password';
import { TableModule } from 'primeng/table';
import { Toast } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Permissions } from '../../../constants/permissions';
import {
  IPermissionGroupList,
  IPermissionMappedWithGroup,
} from '../../../models/permission';
import {
  IAddNewUser,
  ICrudUserList,
  IUserPermissionRow,
} from '../../../models/user';
import { Auth } from '../../../services/auth';
import { Permission } from '../../../services/permission';
import { User } from '../../../services/user';

@Component({
  selector: 'app-users',
  imports: [
    TableModule,
    InputIconModule,
    Toast,
    IconFieldModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    ConfirmDialog,
    MultiSelectModule,
    Dialog,
    ToggleSwitchModule,
    PasswordModule,
  ],
  templateUrl: './users.html',
  styleUrl: './users.css',
  providers: [ConfirmationService, MessageService],
})
export class Users implements OnInit {
  userService = inject(User);
  permissionService = inject(Permission);
  private messageService = inject(MessageService);
  authService = inject(Auth);
  private confirmationService = inject(ConfirmationService);
  readonly permissions = Permissions;
  userDialog: boolean = false;
  user!: ICrudUserList | IAddNewUser;
  submitted: boolean = false;
  permissionRows: IUserPermissionRow[] = [];

  usersWithGroupNames = computed(() => {
    const users = this.userService.crudUserList();
    const groups = this.permissionService.permissionGroups();

    const groupNameMap = new Map<number, string>(
      groups.map((g) => [g.id, g.name]),
    );

    return users.map((u) => ({
      ...u,
      groupNames: u.group_ids
        .map((id) => groupNameMap.get(id))
        .filter(Boolean) // remove undefined
        .join(', '),
    }));
  });

  notificationLabels: Record<string, string> = {
    lost_box_connection_notification: 'Lost Box Connection',
    daily_notification_overview_notification: 'Daily Overview',
    box_no_updates: 'No box updates',
    box_warning_messages: 'Box Warning Messages',
    monitoring_notification: 'Monitoring',
    protobuf_lost_connection: 'Protobuf lost',
    lost_box_list_notification: 'Lost box list (twice a day)',
    permission_expire_notification: 'Permissions expiring',
    lost_box_more_than_hour_notification: 'Lost Box more than hour',
  };

  ngOnInit(): void {
    this.userService.getCrudUserList();
    this.permissionService.getPermissionsList();
    this.permissionService.getPermissionMappedWithGroupList();
    this.permissionService.getPermissionGroupList();
  }

  addUser() {
    this.user = this._createEmptyUser('new');
    this.userDialog = true;
  }

  editUser(user: ICrudUserList) {
    this.user = structuredClone(user);
    this.permissionRows = this.buildPermissionTable(
      this.permissionService.permissions(),
      this.permissionService.permissionMappedWithGroup(),
      this.user,
    );
    this.userDialog = true;
  }

  hasPermission(permission: Permissions): boolean {
    return this.authService.hasPermission(permission);
  }

  confirmDeleteUser(event: Event, user: ICrudUserList) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Do you want to delete this user?',
      header: 'Danger Zone',
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },

      accept: () => {
        this._deleteUser(user);
      },
    });
  }

  hideDialog() {
    this.userDialog = false;
    this.user = this._createEmptyUser();
    this.permissionRows = [];
    this.submitted = false;
  }

  saveUser() {
    if ('id' in this.user) {
      // Edit user
      this.userService.updateUser(this.user).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successful',
            detail: 'Succesfully edited user',
            life: 3000,
          });
          this.hideDialog();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Editing user failed. Please try again',
            life: 3000,
          });
        },
      });
    } else {
      // New user
      this.userService.addUser(this.user).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successful',
            detail: 'Succesfully added new user!',
            life: 3000,
          });
          this.hideDialog();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Saving new user failed, please try again',
            life: 3000,
          });
        },
      });
    }
  }

  sentChangePasswordMail() {
    if ('reset_token_expire' in this.user) {
      this.user.reset_token_expire = new Date(
        new Date().getTime() + 15 * 60000,
      ).toString();

      this.userService.sentChangePasswordMail(this.user).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successful',
            detail: 'Reset mail sent succesfully!',
            life: 3000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Send mail failed',
            life: 3000,
          });
          if ('reset_token_expire' in this.user) {
            this.user.reset_token_expire = '';
          }
        },
      });
    }
  }

  getChangePasswordDisabled(): boolean {
    if ('reset_token_expire' in this.user) {
      if (!this.user.reset_token_expire) {
        return false;
      }

      if (new Date(this.user.reset_token_expire) < new Date()) {
        return false;
      }
    }

    return true;
  }

  private _createEmptyUser(mode: string = 'edit'): ICrudUserList | IAddNewUser {
    if (mode === 'new') {
      return {
        username: '',
        email: '',
        last_login: '',
        group_ids: [],
        password: '',
      };
    } else {
      return {
        id: 0,
        username: '',
        email: '',
        last_login: '',
        reset_token_expire: '',
        notifications: [],
        group_ids: [],
        direct_permissions: [],
      };
    }
  }

  private _deleteUser(user: ICrudUserList) {
    this.userService.deleteUser(user.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successful',
          detail: 'Succesfully removed user!',
          life: 3000,
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Deleting user failed, please try again',
          life: 3000,
        });
      },
    });
  }

  buildPermissionTable(
    allPermissions: IPermissionGroupList[],
    groupPermissions: IPermissionMappedWithGroup[],
    user: ICrudUserList,
  ): IUserPermissionRow[] {
    return allPermissions.map((permission) => {
      // Check if any of the user's groups grant this permission
      const grantedByGroup = groupPermissions.some(
        (gp) =>
          user.group_ids.includes(gp.group_id) &&
          gp.permission_id === permission.id,
      );

      // Check if user has direct permission
      const direct = user.direct_permissions.find(
        (dp) => dp.permission_id === permission.id,
      );

      return {
        permissionId: permission.id,
        permissionName: permission.name,
        grantedByGroup,
        description: permission.description,
        directOverride: !!direct,
        validUntil: direct?.valid_until ?? null,
      };
    });
  }

  getPasswordsMatch(): boolean {
    if ('password' in this.user) {
      return (
        this.user.repeatPass !== undefined &&
        this.user.repeatPass !== this.user.password
      );
    }

    return false;
  }

  getDisableSaveBtn(): boolean {
    if ('id' in this.user) {
      if (
        !this.user.email ||
        !this.user.username ||
        this.user.group_ids.length < 1
      ) {
        return true;
      }
    } else {
      if (
        !this.user.email ||
        !this.user.password ||
        this.user.group_ids.length < 1 ||
        !this.user.username ||
        this.getPasswordsMatch() ||
        this.user.password.length < 8 ||
        this.user.repeatPass!.length < 8
      ) {
        return true;
      }
    }
    return false;
  }
}
